import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Button, Checkbox, Col, DatePicker, Form, Input, Row, Select } from 'antd';
import InputNumberFull from '../atoms/InputNumberFull';
import RowSection from '../atoms/RowSection';
import styled from 'styled-components';
import { FormInstance } from 'antd/lib/form';
import { State } from '../../interfaces/State';
import personalDataRepo from '../../services/repository/personalData';
import { PersonalDocument } from '../../interfaces/PersonalDocument';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import { events } from '../../services/util/field-rules-manager/FieldRuleService';
import { AdmissionDataRules } from '../../services/util/field-rules-manager/types/fieldRuleTypes';
import { CountryBirth } from '../../interfaces/CountryBirth';
import AdmissionFormItem from '../molecules/AdmissionFormItem';
import { Nationality } from '../../interfaces/Nationality';
import { ForeignerEntryCondition } from '../../interfaces/ForeignerEntryCondition';
import BooleanOptionList from '../../constants/BooleanOptionList';
import { DriverLicenseCategory } from '../../interfaces/DriverLicenseCategory';
import { removePunctuation } from '../../services/util/field-rules-manager/normalizers';
import { isScreenUnderWidth } from '../../services/util/screen';

dayjs.extend(weekday);
dayjs.extend(localeData);

const { Option } = Select;

const ButtonCol = styled(Col)`
    display: flex;
    justify-content: flex-end;
    margin-top: 32px;
`;

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
};

type Props = {
  handleNext: () => void,
  initForm: PersonalDocument | undefined,
  admissionDataRules: AdmissionDataRules
};

const PersonalDocuments = React.forwardRef<FormInstance, Props>(({ handleNext, initForm, admissionDataRules }, ref) => {
  const [form] = Form.useForm<PersonalDocument | undefined>();
  const formRules = admissionDataRules.personalDocuments;
  const currentDate = dayjs();

  // @ts-ignore
  useImperativeHandle(ref, () => ({
    validateFields: () => form.validateFields(),
    getFieldsValue: form.getFieldsValue,
    submit: () => form.submit(),
  }));

  const [data, setData] = useState<{
    countries: CountryBirth[],
    foreignerEntryConditions: ForeignerEntryCondition[],
    nationalities: Nationality[],
    states: State[]
    booleanOptionlist: any[],
    driverLicenseCategories: DriverLicenseCategory[],
  }>({
    countries: [],
    states: [],
    nationalities: [],
    foreignerEntryConditions: [],
    booleanOptionlist: [],
    driverLicenseCategories: [],
  });


  const fetchData = async () => {
    try {
      const [
        countriesResponse,
        stateResponse,
        nationalitiesResponse,
        foreignerEntryConditionsResponse,
        driverLicenseCategoriesResponse] = await Promise.all([
        personalDataRepo.findAllCountries(),
        personalDataRepo.findAllStates(),
        personalDataRepo.findAllNationalities(),
        personalDataRepo.findAllForeignerEntryConditions(),
        personalDataRepo.findAllDriverLicenseCategories(),
      ]);

      setData({
        countries: countriesResponse.data,
        states: stateResponse.data,
        nationalities: nationalitiesResponse.data,
        foreignerEntryConditions: foreignerEntryConditionsResponse.data,
        booleanOptionlist: BooleanOptionList,
        driverLicenseCategories: driverLicenseCategoriesResponse,
      });
    } catch (error) {
      console.error('Ocorreu um erro ao buscar os dados:', error);
    }
  };

  useEffect(() => {
    fetchData().then(r => r);
  }, []);

  const handleIdCardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Remove qualquer caractere que não seja número
    const cleanedValue = value.replace(/\D/g, '');
    // Aplica a máscara para o RG (XX.XXX.XXX-X)
    const maskedValue = cleanedValue
      .replace(/^(\d{2})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4')
      .substr(0, 12); // Limita a 12 caracteres (2 dígitos do estado, 9 dígitos do RG e o dígito verificador)
    form.setFieldValue('idCard', maskedValue);
  };

  useEffect(() => {
    if (initForm && initForm.idCardIssuanceDate != null) {
      initForm.idCardIssuanceDate = dayjs(initForm.idCardIssuanceDate);
    }

    if (initForm && initForm.ricExpeditionDate != null) {
      initForm.ricExpeditionDate = dayjs(initForm.ricExpeditionDate);
    }

    if (initForm && initForm.driverLicenseExpeditionDate != null) {
      initForm.driverLicenseExpeditionDate = dayjs(initForm.driverLicenseExpeditionDate);
    }

    if (initForm && initForm.driverLicenseExpiryDate != null) {
      initForm.driverLicenseExpiryDate = dayjs(initForm.driverLicenseExpiryDate);
    }

    if (initForm && initForm.driverLicenseFirstExpeditionDate != null) {
      initForm.driverLicenseFirstExpeditionDate = dayjs(initForm.driverLicenseFirstExpeditionDate);
    }

    form.setFieldsValue(initForm);
  }, [initForm]);


  //todo verificar de colocar esse handler em cada step ou se vale a pena unificar num método via props
  const onValuesChangeHandler = (changedValues: any, values: PersonalDocument | undefined) => {
    const watchedFields = [
      formRules.foreigner?.name,
      formRules.voterRegistrationNumber?.name,
      formRules.idCard?.name,
      formRules.rneCard?.name,
      formRules.driverLicenseNumber?.name,
      formRules.militaryReserveNumber?.name,
      formRules.ricNumber?.name,
    ];

    if (Object.keys(changedValues).some(changedField => watchedFields.includes(changedField))) {
      events.triggerFormRuleUpdateEvent();
    }
  };

  const isUnder = isScreenUnderWidth(576);

  return (
    <>
      <Form onValuesChange={onValuesChangeHandler} form={form} {...formItemLayout} style={{ maxWidth: 600 }}
        requiredMark={false} layout='vertical'>
        <Row>
          <Col span={8}>
            <AdmissionFormItem fieldRule={formRules.foreigner} formItemProps={{ valuePropName: 'checked' }}>
              <Checkbox>Estrangeiro</Checkbox>
            </AdmissionFormItem>
          </Col>
        </Row>

        {form.getFieldValue('foreigner') === true && (
          <Row id='foreignerCardData' gutter={16}>
            <Col xs={24} sm={12} md={8} lg={8}>
              <AdmissionFormItem fieldRule={formRules.rneCard} formItemProps={{ normalize: removePunctuation }}>
                <Input maxLength={8} />
              </AdmissionFormItem>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <AdmissionFormItem fieldRule={formRules.foreignerRneCountry}>
                <Select
                  showSearch
                  optionFilterProp='children'
                  allowClear
                  filterOption={(input, option) => (
                    option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  )}
                  popupMatchSelectWidth={isUnder}
                  placement='bottomLeft'
                >
                  {data.countries.map((country) => (
                    <Option
                      key={`country-${country.id}`}
                      value={country.id}
                    >
                      {country.country}
                    </Option>
                  ))}
                </Select>
              </AdmissionFormItem>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <AdmissionFormItem fieldRule={formRules.rneCardIssuanceDate}>
                <DatePicker
                  format='DD/MM/YYYY'
                  style={{ width: '100%' }}
                  maxDate={currentDate}
                  popupClassName={isUnder ? 'small-date-picker' : ''}
                />
              </AdmissionFormItem>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <AdmissionFormItem fieldRule={formRules.rneCardIssuingAuthority}>
                <Input />
              </AdmissionFormItem>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <AdmissionFormItem fieldRule={formRules.foreignerCountryArrivalDate}>
                <DatePicker
                  format='DD/MM/YYYY'
                  style={{ width: '100%' }}
                  maxDate={currentDate}
                  popupClassName={isUnder ? 'small-date-picker' : ''}
                />
              </AdmissionFormItem>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <AdmissionFormItem fieldRule={formRules.foreignerNationalityCode}>
                <Select
                  showSearch
                  optionFilterProp='children'
                  allowClear
                  filterOption={(input, option) => (
                    option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  )}
                  popupMatchSelectWidth={false}
                >
                  {data.nationalities.map((nationality) => (
                    <Option
                      key={`nationality-${nationality.id}`}
                      value={nationality.id}
                    >
                      {nationality.description}
                    </Option>
                  ))}
                </Select>
              </AdmissionFormItem>
            </Col>

            <Col xs={24} sm={12} md={8} lg={8}>
              <AdmissionFormItem fieldRule={formRules.foreignerEntryCondition}>
                <Select
                  showSearch
                  optionFilterProp='children'
                  allowClear
                  filterOption={(input, option) => (
                    option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  )}
                  popupMatchSelectWidth={false}
                >
                  {data.foreignerEntryConditions.map((foreignerEntryCondition) => (
                    <Option
                      key={`foreignerEntryCondition-${foreignerEntryCondition.id}`}
                      value={foreignerEntryCondition.id}
                    >
                      {foreignerEntryCondition.description}
                    </Option>
                  ))}
                </Select>
              </AdmissionFormItem>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <AdmissionFormItem fieldRule={formRules.foreignerIsMarriedWithBrazilian}>
                <Select
                  showSearch
                  optionFilterProp='children'
                  allowClear
                  popupMatchSelectWidth={false}
                >
                  {data.booleanOptionlist.map(option => (
                    <Option
                      key={option.description}
                      value={option.value}
                    >
                      {option.description}
                    </Option>
                  ))}
                </Select>
              </AdmissionFormItem>
            </Col>
            <Col xs={24} sm={12} md={8} lg={8}>
              <AdmissionFormItem fieldRule={formRules.foreignerHasChildrenWithBrazilian}>
                <Select
                  showSearch
                  optionFilterProp='children'
                  allowClear
                  popupMatchSelectWidth={false}
                >
                  {data.booleanOptionlist.map(option => (
                    <Option
                      key={option.description}
                      value={option.value}
                    >
                      {option.description}
                    </Option>
                  ))}
                </Select>
              </AdmissionFormItem>
            </Col>
          </Row>
        )}
        <Row><RowSection>RG</RowSection></Row>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem
              fieldRule={formRules.idCard}>
              <Input placeholder='Digite aqui' onChange={handleIdCardChange} maxLength={30} />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.idCardIssuingState}>
              <Select
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={false}
              >
                {data.states.map((state) => (
                  <Option
                    key={`idCardIssuingState-${state.id}`}
                    value={state.id}
                  >
                    {state.id}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.idCardIssuingAuthority}>
              <Input />
            </AdmissionFormItem>
          </Col>


          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.idCardIssuanceDate}>
              <DatePicker
                format='DD/MM/YYYY'
                style={{ width: '100%' }}
                maxDate={currentDate}
                popupClassName={isUnder ? 'small-date-picker' : ''}
              />
            </AdmissionFormItem>
          </Col>
        </Row>


        <Row><RowSection>Titulo de Eleitor</RowSection></Row>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.voterRegistrationNumber} formItemProps={{ trigger: 'onBlur' }}>
              <InputNumberFull maxLength={12} />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.voterRegistrationZone}>
              <Input />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.voterRegistrationSection}>
              <Input />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.voterRegistrationState}>
              <Select
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={false}
              >
                {data.states.map((state) => (
                  <Option key={`voterRegistrationState-${state.id}`} value={state.id}>
                    {state.id}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>
        </Row>

        <Row><RowSection>RIC</RowSection></Row>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.ricNumber}>
              <InputNumberFull maxLength={12} />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.ricIssuingAuthority}>
              <Input />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.ricExpeditionDate}>
              <DatePicker
                format='DD/MM/YYYY'
                style={{ width: '100%' }}
                maxDate={currentDate}
                popupClassName={isUnder ? 'small-date-picker' : ''}
              />
            </AdmissionFormItem>
          </Col>
        </Row>

        <Row><RowSection>Habilitação</RowSection></Row>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.driverLicenseNumber} formItemProps={{trigger: 'onBlur'}}>
              <InputNumberFull maxLength={11} />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.driverLicenseExpiryDate}>
              <DatePicker
                format='DD/MM/YYYY'
                style={{ width: '100%' }}
                popupClassName={isUnder ? 'small-date-picker' : ''}
              />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.driverLicenseExpeditionDate}>
              <DatePicker
                format='DD/MM/YYYY'
                style={{ width: '100%' }}
                maxDate={currentDate}
                popupClassName={isUnder ? 'small-date-picker' : ''}
              />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.driverLicenseState}>
              <Select
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={false}
              >
                {data.states.map((state) => (
                  <Option key={`driverLicenseState-${state.id}`} value={state.id}>
                    {state.id}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.driverLicenseFirstExpeditionDate}>
              <DatePicker
                format='DD/MM/YYYY'
                style={{ width: '100%' }}
                maxDate={currentDate}
                popupClassName={isUnder ? 'small-date-picker' : ''}
              />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={12} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.driverLicenseCategory}>
              <Select
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={false}
              >
                {data.driverLicenseCategories.map((driverLicenseCategory) => (
                  <Option key={`driverLicenseCategory-${driverLicenseCategory.id}`} value={driverLicenseCategory.id}>
                    {driverLicenseCategory.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

        </Row>

        { !formRules.militaryReserveNumber?.hidden && (
          <>
            <Row><RowSection>Reservista</RowSection></Row>

            <Row gutter={16}>
              <Col xs={24} sm={12} md={8} lg={8}>
                <AdmissionFormItem fieldRule={formRules.militaryReserveNumber} formItemProps={{trigger: 'onBlur'}}>
                  <InputNumberFull maxLength={12} />
                </AdmissionFormItem>
              </Col>


              <Col xs={24} sm={12} md={8} lg={8}>
                <AdmissionFormItem fieldRule={formRules.militaryReserveSeries}>
                  <Input maxLength={1} />
                </AdmissionFormItem>
              </Col>

              <Col xs={24} sm={12} md={8} lg={8}>
                <AdmissionFormItem fieldRule={formRules.militaryReserveCategory}>
                  <Input />
                </AdmissionFormItem>
              </Col>


              <Col xs={24} sm={12} md={8} lg={8}>
                <AdmissionFormItem fieldRule={formRules.militaryReserveState}>
                  <Select
                    showSearch
                    optionFilterProp='children'
                    allowClear
                    filterOption={(input, option) => (
                      option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    )}
                    popupMatchSelectWidth={false}
                  >
                    {data.states.map((state) => (
                      <Option key={`militaryReserveState-${state.id}`} value={state.id}>
                        {state.id}
                      </Option>
                    ))}
                  </Select>
                </AdmissionFormItem>
              </Col>
            </Row>

          </>
        )}
      </Form>

      <Row>
        <ButtonCol span={24}> <Button type={'primary'}
          onClick={() => handleNext()}>Prosseguir</Button></ButtonCol>
      </Row>
    </>
  );

});

PersonalDocuments.displayName = 'PersonalDocuments';


export default PersonalDocuments;
